import { Link, router, usePage } from "@inertiajs/react"
import { Building2, Clock, FileText, FolderOpen, LayoutDashboard, Moon, Search, Shield, Sun, Users, Wallet, type LucideIcon } from "lucide-react"
import { useMemo, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { statusDot } from "@/components/steel/status"
import { useTheme } from "@/components/theme-provider"
import type { ShellProps } from "@/types"

const NAV: { label: string; href: string; icon: LucideIcon; count?: keyof ShellProps["counts"] }[] = [
  { label: "Overview", href: "/", icon: LayoutDashboard },
  { label: "Bids", href: "/bids", icon: FileText, count: "bids" },
  { label: "Projects", href: "/projects", icon: FolderOpen, count: "projects" },
  { label: "Employees", href: "/team/employees", icon: Users, count: "employees" },
  { label: "Time", href: "/team/time", icon: Clock, count: "pendingTime" },
  { label: "Payroll", href: "/team/payroll", icon: Wallet },
  { label: "Users", href: "/users", icon: Shield, count: "users" },
]

function JumpTo({ items }: { items: ShellProps["search"] }) {
  const [query, setQuery] = useState("")
  const [open, setOpen] = useState(false)
  const blurTimer = useRef<number | undefined>(undefined)

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return []
    return items
      .filter((i) => i.label.toLowerCase().includes(q) || (i.hint ?? "").toLowerCase().includes(q))
      .slice(0, 7)
  }, [items, query])

  const go = (href: string) => {
    setQuery("")
    setOpen(false)
    router.visit(href)
  }

  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          blurTimer.current = window.setTimeout(() => setOpen(false), 120)
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false)
          if (e.key === "Enter" && matches.length) go(matches[0].href)
        }}
        placeholder="Search or jump to"
        className="h-[34px] w-full rounded-md border border-input bg-background pl-8 pr-2.5 text-label placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />

      {open && matches.length > 0 ? (
        <div className="absolute left-0 right-0 top-[38px] z-30 overflow-hidden rounded-md border border-border bg-popover shadow-lg">
          {matches.map((m) => (
            <button
              key={`${m.kind}-${m.href}`}
              type="button"
              onMouseDown={() => window.clearTimeout(blurTimer.current)}
              onClick={() => go(m.href)}
              className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-secondary"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-label font-medium">{m.label}</span>
                {m.hint ? <span className="block truncate text-micro text-muted-foreground">{m.hint}</span> : null}
              </span>
              <span className="shrink-0 text-micro text-muted-foreground">{m.kind}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function AppSidebar() {
  const { props, url } = usePage<{ shell: ShellProps }>()
  const { counts, recents, currentUser, search } = props.shell
  const { theme, toggleTheme } = useTheme()

  const path = (url.split("?")[0] || "/").replace(/\/+$/, "") || "/"
  const isActive = (href: string) => (href === "/" ? path === "/" : path.startsWith(href))

  return (
    <aside className="flex w-[228px] shrink-0 flex-col border-r border-border bg-background">
      <div className="flex items-center gap-2.5 px-4 pb-3 pt-3.5">
        <span className="flex h-[26px] w-[26px] items-center justify-center rounded-md bg-primary text-primary-foreground">
          <Building2 className="h-[15px] w-[15px]" strokeWidth={1.75} />
        </span>
        <span className="text-label font-semibold tracking-[-0.01em]">SteelTrack</span>
      </div>

      <div className="px-3 pb-2.5">
        <JumpTo items={search} />
      </div>

      <nav className="flex-1 overflow-y-auto px-2">
        {NAV.map((item) => {
          const active = isActive(item.href)
          const count = item.count ? counts[item.count] : 0
          return (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                "mb-px flex h-[30px] items-center gap-2.5 rounded-md px-2 text-label transition-colors hover:bg-secondary",
                active ? "bg-secondary font-semibold" : "font-normal",
              )}
            >
              <item.icon className="h-[15px] w-[15px] shrink-0 opacity-85" strokeWidth={1.75} />
              <span className="flex-1">{item.label}</span>
              {count ? <span className="num text-meta text-muted-foreground">{count}</span> : null}
            </Link>
          )
        })}

        {recents.length > 0 ? (
          <div>
            <div className="mx-2 my-2.5 h-px bg-border" />
            <p className="eyebrow mx-2 mb-1.5">Recent</p>
            {recents.map((r) => (
              <Link
                key={r.href}
                href={r.href}
                className="mb-px flex h-7 items-center gap-2.5 rounded-md px-2 text-meta transition-colors hover:bg-secondary"
              >
                <span
                  className="h-[5px] w-[5px] shrink-0 rounded-full"
                  style={{ background: statusDot(r.status) }}
                />
                <span className="flex-1 truncate">{r.label}</span>
              </Link>
            ))}
          </div>
        ) : null}
      </nav>

      <div className="border-t border-border p-2">
        <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary text-[10.5px] font-semibold">
            {currentUser?.initials ?? "—"}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-label font-medium">{currentUser?.name ?? "Signed out"}</p>
            <p className="text-micro text-muted-foreground">Admin</p>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={toggleTheme}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          >
            {theme === "dark" ? <Sun /> : <Moon />}
          </Button>
        </div>
      </div>
    </aside>
  )
}
